import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  Code as CodeIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Pagination } from '../components/Pagination';
import { useProjectContext } from '../contexts/ProjectContext';

interface Script {
  id: string;
  name: string;
  description: string;
  script_type: string;
  status: string;
  created_at: string;
  project_name: string;
  first_name?: string;
  last_name?: string;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
  totalItems: number;
}

export const ScriptsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedProject } = useProjectContext();
  const [scripts, setScripts] = useState<Script[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    totalItems: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchScripts = async (page: number = 1) => {
    // Don't fetch if no project is selected
    if (!selectedProject) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const projectId = selectedProject.id;

      const response = await fetch(`/api/scripts/paginated?page=${page}&limit=10&project_id=${projectId}`, { headers });
      const data = await response.json();
      
      if (data.success) {
        setScripts(data.data.items || []);
        setPagination(data.data.pagination);
      } else {
        setError(data.error || 'Failed to load scripts');
      }
    } catch (err) {
      setError('Failed to load scripts');
      console.error('Scripts fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScripts(1);
  }, [selectedProject]);

  const handlePageChange = (page: number) => {
    fetchScripts(page);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
      case 'completed':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'pending':
      case 'initializing':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
        return <PlayIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      case 'failed':
      case 'error':
        return <ErrorIcon />;
      case 'pending':
      case 'initializing':
        return <ScheduleIcon />;
      default:
        return <HistoryIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScriptTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'jmeter': 'JMeter',
      'har': 'HAR',
      'postman': 'Postman',
      'swagger': 'Swagger',
      'custom': 'Custom'
    };
    return typeMap[type] || type;
  };

  const getUserInitials = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    return 'U';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          All Scripts
        </Typography>
      </Box>

      {!selectedProject && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Please select or create a project to view scripts.
          </Typography>
          <Button 
            variant="contained" 
            size="small" 
            onClick={() => navigate('/dashboard')}
            sx={{ mt: 1 }}
          >
            Go to Dashboard
          </Button>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {selectedProject && (
        <Paper elevation={2}>
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            hasNext={pagination.hasNext}
            hasPrevious={pagination.hasPrevious}
            totalItems={pagination.totalItems}
            onPageChange={handlePageChange}
          />
          <List>
            {scripts.map((script, index) => (
              <React.Fragment key={script.id}>
                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <CodeIcon />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" component="span">
                          {script.name}
                        </Typography>
                        <Chip
                          label={getScriptTypeLabel(script.script_type)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={script.status}
                          size="small"
                          color={getStatusColor(script.status) as any}
                          icon={getStatusIcon(script.status)}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {script.description || 'No description'}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="caption" color="text.secondary">
                            Project: {script.project_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Created: {formatDate(script.created_at)}
                          </Typography>
                          {script.first_name && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }}>
                                {getUserInitials(script.first_name, script.last_name)}
                              </Avatar>
                              <Typography variant="caption" color="text.secondary">
                                {script.first_name} {script.last_name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  <Box display="flex" gap={1}>
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <CodeIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
                {index < scripts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
          
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            hasNext={pagination.hasNext}
            hasPrevious={pagination.hasPrevious}
            totalItems={pagination.totalItems}
            onPageChange={handlePageChange}
          />
        </Paper>
      )}
    </Box>
  );
}; 